import React, { useState, useEffect } from 'react';
import { Camera, X } from 'lucide-react';

/**
 * AIImageDisclaimer - Prominent centered banner informing users 
 * that images are illustrative and they should follow dish descriptions.
 * Shows once per session, can be dismissed.
 */
const AIImageDisclaimer = ({ language = 'es' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);

  const translations = {
    es: {
      title: 'Las imágenes son orientativas',
      emphasis: 'Sigue siempre la descripción de cada plato',
      message: 'Las fotografías pueden no representar exactamente el producto final. La descripción del plato es la referencia real de lo que vas a recibir.',
      dismiss: 'Entendido',
    },
    en: {
      title: 'Images are illustrative',
      emphasis: 'Always follow the dish description',
      message: 'Photographs may not exactly represent the final product. The dish description is the real reference of what you will receive.',
      dismiss: 'Got it',
    },
    fr: {
      title: 'Images à titre indicatif',
      emphasis: 'Suivez toujours la description du plat',
      message: 'Les photographies peuvent ne pas représenter exactement le produit final. La description du plat est la référence réelle de ce que vous allez recevoir.',
      dismiss: 'Compris',
    },
    cat: {
      title: 'Les imatges són orientatives',
      emphasis: "Segueix sempre la descripció de cada plat",
      message: "Les fotografies poden no representar exactament el producte final. La descripció del plat és la referència real del que rebràs.",
      dismiss: 'Entès',
    },
  };

  const t = translations[language] || translations.es;

  useEffect(() => {
    const dismissed = sessionStorage.getItem('ai-disclaimer-dismissed');
    if (dismissed) return;

    // Check admin toggle before showing
    const API = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API}/api/admin/image-disclaimer`)
      .then(r => r.ok ? r.json() : { enabled: true })
      .then(({ enabled }) => {
        if (enabled === false) return; // Admin disabled the banner globally
        const timer = setTimeout(() => setIsVisible(true), 600);
        return () => clearTimeout(timer);
      })
      .catch(() => {
        // On error, fall back to default behavior (show)
        setTimeout(() => setIsVisible(true), 600);
      });
  }, []);

  const handleDismiss = () => {
    setIsAnimatingOut(true);
    sessionStorage.setItem('ai-disclaimer-dismissed', 'true');
    setTimeout(() => setIsVisible(false), 300);
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={handleDismiss}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 999,
          background: 'rgba(0,0,0,0.6)',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
          animation: isAnimatingOut ? 'fadeOut 0.3s ease forwards' : 'fadeIn 0.3s ease',
        }}
      />

      {/* Centered modal */}
      <div
        data-testid="ai-image-disclaimer"
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1000,
          width: 'calc(100% - 48px)',
          maxWidth: '380px',
          animation: isAnimatingOut
            ? 'disclaimerOut 0.3s ease forwards'
            : 'disclaimerIn 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >
        <div
          style={{
            background: 'linear-gradient(160deg, #2C2820 0%, #3A3428 50%, #2C2820 100%)',
            borderRadius: '24px',
            padding: '32px 24px 24px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(183,140,60,0.25)',
            textAlign: 'center',
          }}
        >
          {/* Close */}
          <button
            onClick={handleDismiss}
            data-testid="ai-disclaimer-close"
            style={{
              position: 'absolute',
              top: '12px',
              right: '12px',
              background: 'rgba(255,255,255,0.08)',
              border: 'none',
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: 'rgba(245,241,235,0.5)',
            }}
          >
            <X size={16} />
          </button>

          {/* Icon */}
          <div
            style={{
              width: '56px',
              height: '56px',
              borderRadius: '16px',
              background: 'linear-gradient(135deg, #B78C3C 0%, #D4AF37 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              boxShadow: '0 4px 16px rgba(183,140,60,0.3)',
            }}
          >
            <Camera size={26} color="#FBF5E8" />
          </div>

          {/* Title */}
          <h3
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: '22px',
              fontWeight: 700,
              color: '#D4AF37',
              margin: '0 0 12px',
              lineHeight: 1.25,
            }}
          >
            {t.title}
          </h3>

          {/* Emphasis line */}
          <div
            style={{
              background: 'linear-gradient(135deg, rgba(183,140,60,0.15) 0%, rgba(212,175,55,0.1) 100%)',
              border: '1px solid rgba(212,175,55,0.25)',
              borderRadius: '12px',
              padding: '12px 16px',
              margin: '0 0 16px',
            }}
          >
            <p
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '16px',
                fontWeight: 700,
                color: '#F0E4CE',
                margin: 0,
                lineHeight: 1.4,
              }}
            >
              {t.emphasis}
            </p>
          </div>

          {/* Description */}
          <p
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '14px',
              lineHeight: 1.6,
              color: 'rgba(245, 241, 235, 0.7)',
              margin: '0 0 24px',
            }}
          >
            {t.message}
          </p>

          {/* CTA Button */}
          <button
            onClick={handleDismiss}
            data-testid="ai-disclaimer-dismiss"
            style={{
              width: '100%',
              padding: '14px 20px',
              background: 'linear-gradient(135deg, #B78C3C 0%, #D4AF37 100%)',
              border: 'none',
              borderRadius: '14px',
              color: '#2C2820',
              fontSize: '16px',
              fontWeight: 700,
              cursor: 'pointer',
              fontFamily: "'DM Sans', sans-serif",
              letterSpacing: '0.02em',
              boxShadow: '0 4px 16px rgba(183,140,60,0.35)',
              transition: 'transform 0.2s, box-shadow 0.2s',
            }}
          >
            {t.dismiss}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes disclaimerIn {
          from { opacity: 0; transform: translate(-50%, -45%) scale(0.95); }
          to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
        }
        @keyframes disclaimerOut {
          from { opacity: 1; transform: translate(-50%, -50%) scale(1); }
          to { opacity: 0; transform: translate(-50%, -55%) scale(0.95); }
        }
      `}</style>
    </>
  );
};

export default AIImageDisclaimer;
